import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiX, FiAward, FiCheck } from 'react-icons/fi';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getLastWeekendStr = (targetDay = 0) => { // 0 = Sunday, 6 = Saturday
  const d = new Date();
  const currentDay = d.getDay();
  let diff = (currentDay - targetDay + 7) % 7;
  if (diff === 0) diff = 7;
  d.setDate(d.getDate() - diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function CreditEarnedLeaveModal({
  isOpen,
  onClose,
  subordinates = [],
  preSelectedEmp = null,
  onSuccess,
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [workDate, setWorkDate] = useState(getYesterdayStr());
  const [days, setDays] = useState(1);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (preSelectedEmp) {
      const empId = typeof preSelectedEmp === 'string' ? preSelectedEmp : preSelectedEmp.employeeId;
      setEmployeeId(empId || '');
    } else {
      setEmployeeId(subordinates.length > 0 ? (subordinates[0].employeeId || '') : '');
    }
    setWorkDate(getYesterdayStr());
    setDays(1);
    setReason('');
  }, [isOpen, preSelectedEmp, subordinates]);

  const selectedSub = useMemo(() => {
    return subordinates.find((s) => s.employeeId === employeeId);
  }, [subordinates, employeeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) {
      toast.error('Please select an employee.');
      return;
    }
    if (!workDate) {
      toast.error('Please enter the date worked.');
      return;
    }
    if (Number(days) !== 1) {
      toast.error('A manager can only grant 1 earned leave day at a time.');
      return;
    }
    if (!reason || !reason.trim()) {
      toast.error('Please provide a reason / justification for earned leave credit.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        employeeId,
        workDate,
        days: 1,
        reason: reason.trim(),
      };

      const res = await api.post('/leave/manager/earned-leave', payload);
      const data = res.data?.data || res.data;
      toast.success(
        `Successfully added 1 day earned leave to ${selectedSub?.fullName || employeeId}!`,
        { icon: '🎖️', duration: 4000 }
      );
      if (onSuccess) onSuccess(data);
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to add earned leave.';
      toast.error(typeof msg === 'string' ? msg : 'Error adding earned leave.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const drawerNode = (
    <div className="fixed inset-0 z-[99999] overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-xs transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Slide-over sidebar panel from the right */}
      <div className="fixed inset-y-0 right-0 max-w-full flex pl-0 sm:pl-8">
        <div className="w-screen max-w-lg bg-white shadow-2xl flex flex-col transform transition-transform duration-200 ease-out border-l border-gray-200">
          
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between shrink-0 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#008cd6] text-white flex items-center justify-center shadow-md shadow-sky-100 shrink-0">
                <FiAward size={20} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Add Earned Leave</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Credit approved earned leave from weekend or holiday duty
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
              title="Close panel"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Form Body with generous spacing */}
          <form id="credit-earned-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6 text-xs">
            
            {/* 1. Direct Report / Team Member Selection */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Direct Report <span className="text-rose-500">*</span>
              </label>
              {subordinates.length > 0 ? (
                <select
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  disabled={submitting}
                  required
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:border-[#008cd6] transition-colors cursor-pointer"
                >
                  <option value="">-- Select subordinate --</option>
                  {subordinates.map((sub) => (
                    <option key={sub.id || sub.employeeId} value={sub.employeeId}>
                      {sub.fullName || sub.username || sub.name || sub.employeeId} (#{sub.employeeId}) &mdash; {sub.department || sub.jobTitle || 'Team'}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="Enter Employee ID (e.g., EMP001)"
                  disabled={submitting}
                  required
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:border-[#008cd6] transition-colors"
                />
              )}
              {selectedSub && (
                <div className="pt-1 text-[11px] text-gray-500 flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-gray-800">{selectedSub.fullName || selectedSub.username || selectedSub.name}</span>
                  <span>&bull;</span>
                  <span className="font-mono text-gray-500">#{selectedSub.employeeId}</span>
                  {selectedSub.department && (
                    <>
                      <span>&bull;</span>
                      <span>{selectedSub.department}</span>
                    </>
                  )}
                  {selectedSub.earnedLeave != null && (
                    <>
                      <span>&bull;</span>
                      <span className="text-[#008cd6] font-semibold">{selectedSub.earnedLeave}d current earned</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 2. Date Worked & Presets */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date Worked <span className="text-rose-500">*</span>
                </label>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                  <span className="text-slate-400">Presets:</span>
                  <button
                    type="button"
                    onClick={() => setWorkDate(getLastWeekendStr(6))}
                    className="hover:text-[#008cd6] hover:underline cursor-pointer"
                  >
                    Last Sat
                  </button>
                  <span>&bull;</span>
                  <button
                    type="button"
                    onClick={() => setWorkDate(getLastWeekendStr(0))}
                    className="hover:text-[#008cd6] hover:underline cursor-pointer"
                  >
                    Last Sun
                  </button>
                  <span>&bull;</span>
                  <button
                    type="button"
                    onClick={() => setWorkDate(getYesterdayStr())}
                    className="hover:text-[#008cd6] hover:underline cursor-pointer"
                  >
                    Yesterday
                  </button>
                </div>
              </div>

              <input
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
                max={getYesterdayStr()}
                disabled={submitting}
                required
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-900 focus:outline-none focus:border-[#008cd6] transition-colors"
              />
              <p className="text-[11px] text-gray-400">
                Earned leave can be credited for approved work completed on past weekends or holidays.
              </p>
            </div>

            {/* 3. Reason / Justification */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Work Description / Reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Supported client production deployment over the weekend; completed urgent milestone deliveries on holiday."
                disabled={submitting}
                required
                className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:border-[#008cd6] leading-relaxed resize-none"
              />
            </div>

          </form>

          {/* Fixed Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/80 flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-200/70 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="credit-earned-form"
              disabled={submitting || !employeeId}
              className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-[#008cd6] hover:bg-[#0070b0] transition-colors disabled:opacity-50 cursor-pointer shadow-xs flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FiCheck size={14} />
                  <span>Add 1 Day Earned Leave</span>
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(drawerNode, document.body) : drawerNode;
}
